import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import { required, Validators } from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from './components/assets/substitution-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncPersistPayment, getPayment } from 'src/store/actions/payment-actions';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDropdown } from 'src/components/redux-form/fields/FieldDropdown';
import { FieldDate } from 'src/components/redux-form/fields/FieldDate';

import { Paginated } from 'src/services/api-handlers/pagination';
import InvoicePaymentGrid from './components/InvoicePaymentGrid';
import { IServiceType } from '../../../../../types/service-types';
import { IInvoice } from '../../../../../types/invoice';
import { FieldArray } from 'redux-form';
import RenderFieldArray from './components/RenderFieldArray';
import { QueryParams } from '../../../../../types/grid';
import { numberWithCommas } from '../../../../../helpers/format';

export interface PaymentFormData {
    id: string | number;
    paymentInvoices: any;
    paymentCredits: any;
    amount: number;
    caseType: SelectObject | undefined;
    eftInfo: string;
    paymentDate: string;
    status: number;
}

interface PaymentFormProps {
    students: SelectObject[];
    vendors: SelectObject[];
    getData: (params: QueryParams) => void;
    invoices: Paginated<IInvoice>;
    districts: SelectObject[];
    providers: SelectObject[];
    stateData: any;
    paymentCredits?: any;
    remainAmount: number;
    amountValue: number;
    caseNumbers: { [key: string]: string | null }[];
    totalAmount: number;
    caseTypeHistory?: any;
    setStateData: (data: any) => void;
    changeValue: (field: string, value: string | null) => void;
    serviceTypes: Paginated<IServiceType>;
    handleSub: (data: (e: any) => void, e: any) => void;
    handleDraftSub: (data: (e: any) => void, e: any) => void;
    isDraft?: boolean;
}

type InjectedProps = InjectedFormProps<PaymentFormData, PaymentFormProps>;

const validators: Validators<PaymentFormData> = {
    paymentDate: [required({ message: 'Payment Date is required.' })],
    eftInfo: [required({ message: 'Check/EFT Info is required.' })],
    amount: [required({ message: 'Amount, $ is required.' })],
};

const PaymentForm: React.FunctionComponent<InjectedProps & PaymentFormProps> = ({
    handleSubmit,
    error,
    students,
    vendors,
    districts,
    getData,
    invoices,
    serviceTypes,
    providers,
    dirty,
    setStateData,
    changeValue,
    stateData,
    caseTypeHistory,
    submitting,
    remainAmount,
    totalAmount,
    amountValue,
    caseNumbers,
    handleSub,
    handleDraftSub,
    isDraft,
    initialValues,
}: InjectedProps & PaymentFormProps) => {
    const isNew = !initialValues || !initialValues.id;

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />

            <div className="d-flex flex-row">
                <div className="w-25 mr-5">
                    <FieldDate
                        name="paymentDate"
                        type="text"
                        validate={validators.paymentDate}
                        date={true}
                        wrapperConfig={{
                            label: 'Payment Date: *',
                        }}
                    />
                </div>
                <div className="w-25 mr-5">
                    <FieldInput
                        name="eftInfo"
                        validate={validators.eftInfo}
                        type="text"
                        wrapperConfig={{
                            label: 'Check/EFT Info: *',
                        }}
                    />
                </div>
                <div className="w-25 mr-5">
                    <FieldInput
                        name="amount"
                        validate={validators.amount}
                        type="number"
                        wrapperConfig={{
                            label: 'Amount, $: *',
                        }}
                    />
                </div>
                <div className="w-25 mr-5">
                    <FieldDropdown
                        name={`caseType`}
                        data={[caseTypeHistory]}
                        disabled={true}
                        textField={'label'}
                        withSearchInput={true}
                        valueField={'value'}
                        wrapperConfig={{
                            label: 'Case Type:',
                        }}
                    />
                </div>
            </div>
            <div className={'list-invoices'}>
                <div className={`${classNames('pageHeadingTitle')}`}>Apply to Cases & Invoices:</div>

                <InvoicePaymentGrid
                    students={students}
                    vendors={vendors}
                    invoices={invoices}
                    districts={districts}
                    caseTypeHistory={caseTypeHistory}
                    getData={getData}
                    providers={providers}
                    serviceTypes={serviceTypes}
                    stateData={stateData}
                    amountValue={amountValue}
                    paymentId={initialValues?.id}
                    caseType={initialValues?.caseType?.value}
                    setStateData={setStateData}
                    changeValue={changeValue}
                />
            </div>
            <div className={'credits'}>
                <div className={`${classNames('pageHeadingTitle')}`}>
                    Sum of the entered values: ${numberWithCommas(totalAmount)}. Remaining amount:{' '}
                    <span> ${numberWithCommas(remainAmount)}</span>
                </div>
                <div className={`${classNames('pageHeadingTitle')}`}>Apply the remaining amount to a credit:</div>
                <FieldArray name="paymentCredits" caseNumbers={caseNumbers} component={RenderFieldArray} />
            </div>

            <div className={classNames(styles.actions)}>
                <button
                    type="button"
                    onClick={(e) => {
                        const submit = handleSubmit(async (data, dispatch, props) => {
                            await asyncPersistPayment(
                                { ...data, status: 1, paymentInvoices: props.stateData },
                                dispatch,
                            );
                        });
                        handleSub(submit, e);
                    }}
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={(!dirty && !isDraft) || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                </button>
                {(isNew || isDraft) && (
                    <button
                        type="button"
                        onClick={(e) => {
                            const submit = handleSubmit(async (data, dispatch, props) => {
                                await asyncPersistPayment(
                                    { ...data, status: 4, paymentInvoices: props.stateData },
                                    dispatch,
                                );
                            });
                            handleDraftSub(submit, e);
                        }}
                        className={classNames('btn', 'btn-warning', 'mr-3')}
                        disabled={!dirty || submitting}
                    >
                        <FontAwesomeIcon icon={faSave} /> Save as Draft
                    </button>
                )}
                <Link className={classNames('btn', 'btn-danger')} to={Router.generate(routes.PAYMENTS_LIST)}>
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

export default reduxForm<PaymentFormData, PaymentFormProps>({
    form: 'payment-form',
    enableReinitialize: true,
    onSubmit: async (data: PaymentFormData, dispatch: Dispatch<any>, props: PaymentFormProps) => {
        await asyncPersistPayment(
            { ...data, paymentInvoices: props.stateData },
            dispatch,
        );
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<PaymentFormData, PaymentFormProps>,
    ) {
        const isNew = !props.initialValues || !props.initialValues.id;

        toast.success(`payment was successfully ${isNew ? 'created' : 'updated'}.`);

        if (!isNew && props.initialValues?.id) {
            dispatch(getPayment(props.initialValues.id));
        }
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(PaymentForm);
